import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Building2, Plus, Search, MoreHorizontal, Pause, Play, Archive,
  Edit, Users, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import { PendingApprovalsPanel } from '../../components/tenants/PendingApprovalsPanel';

const statusColors = {
  active: 'bg-green-100 text-green-700 border-green-200',
  suspended: 'bg-red-100 text-red-700 border-red-200',
  archived: 'bg-gray-100 text-gray-600 border-gray-200',
  draft: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const emptyForm = {
  company_name: '', dba_name: '', email: '', phone: '', website: '',
  address_line_1: '', city: '', state: '', zip: '', timezone: 'America/New_York',
  primary_color: '#3b82f6', accent_color: '#10b981', sidebar_color: '#ffffff',
  logo_url: '', allow_downline_branding: false,
  admin_first_name: '', admin_last_name: '', admin_email: '', admin_password: '',
};

export default function TenantManagement() {
  const [searchParams] = useSearchParams();
  const [tenants, setTenants] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      openCreateDialog();
    }
  }, [searchParams]);

  useEffect(() => {
    let list = tenants;
    if (statusFilter !== 'all') list = list.filter((t) => t.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((t) => t.company_name?.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q));
    }
    setFiltered(list);
  }, [tenants, search, statusFilter]);

  const loadTenants = async () => {
    try {
      const data = await api.getTenants();
      setTenants(data);
    } catch (err) {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditTenant(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (tenant) => {
    setEditTenant(tenant);
    setForm({
      company_name: tenant.company_name || '',
      dba_name: tenant.dba_name || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      website: tenant.website || '',
      address_line_1: tenant.address_line_1 || '',
      city: tenant.city || '',
      state: tenant.state || '',
      zip: tenant.zip || '',
      timezone: tenant.timezone || 'America/New_York',
      primary_color: tenant.primary_color || '#3b82f6',
      accent_color: tenant.accent_color || '#10b981',
      sidebar_color: tenant.sidebar_color || '#ffffff',
      logo_url: tenant.logo_url || '',
      allow_downline_branding: tenant.allow_downline_branding || false,
      admin_first_name: '', admin_last_name: '', admin_email: '', admin_password: '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      if (editTenant) {
        await api.updateTenant(editTenant.id, {
          company_name: form.company_name, dba_name: form.dba_name,
          email: form.email, phone: form.phone, website: form.website,
          address_line_1: form.address_line_1, city: form.city,
          state: form.state, zip: form.zip, timezone: form.timezone,
          primary_color: form.primary_color, accent_color: form.accent_color,
          sidebar_color: form.sidebar_color, logo_url: form.logo_url,
          allow_downline_branding: form.allow_downline_branding,
        });
        toast.success('Tenant updated');
      } else {
        await api.createTenant(form);
        toast.success('Tenant created');
      }
      setDialogOpen(false);
      loadTenants();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusAction = async (tenantId, action) => {
    try {
      if (action === 'suspend') await api.suspendTenant(tenantId);
      else if (action === 'activate') await api.activateTenant(tenantId);
      else if (action === 'archive') await api.archiveTenant(tenantId);
      toast.success(`Tenant ${action}d`);
      loadTenants();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tenant Management</h1>
          <p className="text-sm text-muted-foreground mt-1">{tenants.length} tenants total</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={loadTenants} data-testid="refresh-tenants-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={openCreateDialog} data-testid="create-tenant-btn">
            <Plus className="h-4 w-4 mr-2" /> Create Tenant
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search tenants..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-tenants-input" />
          </div>
          <div className="flex gap-1">
            {['all', 'pending_approval', 'active', 'suspended', 'archived'].map((s) => (
              <Button
                key={s}
                variant={statusFilter === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(s)}
                className={statusFilter === s ? 'bg-[#3b82f6] hover:bg-[#2563eb] text-white' : ''}
                data-testid={`filter-${s}-btn`}
              >
                {{ all: 'All', pending_approval: 'Pending Approval', active: 'Active', suspended: 'Suspended', archived: 'Closed' }[s]}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {statusFilter === 'pending_approval' && <PendingApprovalsPanel onChanged={loadTenants} />}

      {/* Table */}
      <Card className={statusFilter === 'pending_approval' ? 'hidden' : ''}>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="tenants-table">
            <thead>
              <tr className="border-b text-left text-sm text-muted-foreground">
                <th className="px-6 py-3 font-medium">Company</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Active Users</th>
                <th className="px-6 py-3 font-medium">Total Leads</th>
                <th className="px-6 py-3 font-medium">Created</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">No tenants found</td></tr>
              )}
              {filtered.map((t) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-accent/50 transition-colors" data-testid={`tenant-row-${t.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: t.primary_color || '#3b82f6' }}>
                        <Building2 className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <button type="button" onClick={() => navigate(`/admin/tenants/${t.id}`)}
                                className="font-medium text-sm hover:underline text-left" data-testid={`open-tenant-${t.id}`}>{t.company_name}</button>
                        {t.dba_name && <p className="text-xs text-muted-foreground">DBA: {t.dba_name}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[t.status] || statusColors.draft}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">{t.active_users ?? 0}</td>
                  <td className="px-6 py-4 text-sm">{t.total_leads ?? 0}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`tenant-actions-${t.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(t)} data-testid={`edit-tenant-${t.id}`}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        {t.status === 'active' && (
                          <DropdownMenuItem onClick={() => handleStatusAction(t.id, 'suspend')} className="text-red-600" data-testid={`suspend-tenant-${t.id}`}>
                            <Pause className="h-4 w-4 mr-2" /> Suspend
                          </DropdownMenuItem>
                        )}
                        {t.status === 'suspended' && (
                          <DropdownMenuItem onClick={() => handleStatusAction(t.id, 'activate')} className="text-green-600" data-testid={`activate-tenant-${t.id}`}>
                            <Play className="h-4 w-4 mr-2" /> Activate
                          </DropdownMenuItem>
                        )}
                        {t.status !== 'archived' && (
                          <DropdownMenuItem onClick={() => navigate(`/admin/tenants/${t.id}?tab=status`)} data-testid={`archive-tenant-${t.id}`}>
                            <Archive className="h-4 w-4 mr-2" /> Close / Archive…
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTenant ? 'Edit Tenant' : 'Create New Tenant'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Company Info */}
            <div>
              <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Company Information</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company Name *</Label>
                  <Input value={form.company_name} onChange={(e) => setField('company_name', e.target.value)} className="mt-1" data-testid="tenant-form-company-name" />
                </div>
                <div>
                  <Label>DBA Name</Label>
                  <Input value={form.dba_name} onChange={(e) => setField('dba_name', e.target.value)} className="mt-1" data-testid="tenant-form-dba" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className="mt-1" data-testid="tenant-form-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setField('phone', e.target.value)} className="mt-1" data-testid="tenant-form-phone" />
                </div>
                <div className="col-span-2">
                  <Label>Website</Label>
                  <Input value={form.website} onChange={(e) => setField('website', e.target.value)} className="mt-1" />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Address</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Street Address</Label>
                  <Input value={form.address_line_1} onChange={(e) => setField('address_line_1', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={form.city} onChange={(e) => setField('city', e.target.value)} className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>State</Label>
                    <Input value={form.state} onChange={(e) => setField('state', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label>ZIP</Label>
                    <Input value={form.zip} onChange={(e) => setField('zip', e.target.value)} className="mt-1" />
                  </div>
                </div>
              </div>
            </div>

            {/* Admin User (only on create) */}
            {!editTenant && (
              <div>
                <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Initial Admin User (Optional)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input value={form.admin_first_name} onChange={(e) => setField('admin_first_name', e.target.value)} className="mt-1" data-testid="tenant-form-admin-first" />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input value={form.admin_last_name} onChange={(e) => setField('admin_last_name', e.target.value)} className="mt-1" data-testid="tenant-form-admin-last" />
                  </div>
                  <div>
                    <Label>Admin Email</Label>
                    <Input type="email" value={form.admin_email} onChange={(e) => setField('admin_email', e.target.value)} className="mt-1" data-testid="tenant-form-admin-email" />
                  </div>
                  <div>
                    <Label>Admin Password</Label>
                    <Input type="password" value={form.admin_password} onChange={(e) => setField('admin_password', e.target.value)} className="mt-1" data-testid="tenant-form-admin-password" />
                  </div>
                </div>
              </div>
            )}

            {/* Branding */}
            <div>
              <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Branding</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={form.primary_color} onChange={(e) => setField('primary_color', e.target.value)} className="w-9 h-9 rounded border cursor-pointer" data-testid="tenant-brand-primary" />
                    <Input value={form.primary_color} onChange={(e) => setField('primary_color', e.target.value)} className="font-mono text-xs" />
                  </div>
                </div>
                <div>
                  <Label>Accent Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={form.accent_color} onChange={(e) => setField('accent_color', e.target.value)} className="w-9 h-9 rounded border cursor-pointer" data-testid="tenant-brand-accent" />
                    <Input value={form.accent_color} onChange={(e) => setField('accent_color', e.target.value)} className="font-mono text-xs" />
                  </div>
                </div>
                <div>
                  <Label>Sidebar Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={form.sidebar_color} onChange={(e) => setField('sidebar_color', e.target.value)} className="w-9 h-9 rounded border cursor-pointer" data-testid="tenant-brand-sidebar" />
                    <Input value={form.sidebar_color} onChange={(e) => setField('sidebar_color', e.target.value)} className="font-mono text-xs" />
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <Label>Logo URL</Label>
                <Input value={form.logo_url} onChange={(e) => setField('logo_url', e.target.value)} placeholder="https://..." className="mt-1" data-testid="tenant-brand-logo" />
              </div>
              <div className="flex items-center gap-2 mt-4">
                <input
                  type="checkbox"
                  checked={form.allow_downline_branding}
                  onChange={(e) => setField('allow_downline_branding', e.target.checked)}
                  className="rounded"
                  id="allow-downline"
                  data-testid="tenant-brand-allow-downline"
                />
                <label htmlFor="allow-downline" className="text-sm cursor-pointer">Allow agents to customize their own branding</label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={handleSave} disabled={saving} data-testid="save-tenant-btn">
              {saving ? 'Saving...' : editTenant ? 'Update Tenant' : 'Create Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
